import {
  Avatar,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { Database, Package, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGlobalSearch } from "../../api/search";
import { resolveImageUrl } from "../../utils/imageUrl";

const SearchDropdown = ({ isOpen, searchQuery, onItemClick }) => {
  const navigate = useNavigate();
  const { searchResults, loading } = useGlobalSearch(searchQuery);

  const getIcon = (type) => {
    switch (type) {
      case "member":
        return (
          <User
            size={20}
            style={{ color: "var(--mui-palette-text-secondary)" }}
          />
        );
      case "asset":
        return (
          <Package
            size={20}
            style={{ color: "var(--mui-palette-text-secondary)" }}
          />
        );
      case "source":
        return (
          <Database
            size={20}
            style={{ color: "var(--mui-palette-text-secondary)" }}
          />
        );
      default:
        return (
          <User
            size={20}
            style={{ color: "var(--mui-palette-text-secondary)" }}
          />
        );
    }
  };

  const renderItemIcon = (item) => {
    // Handle member avatars
    if (item.type === "member" && item.avatar) {
      return (
        <Avatar
          src={item.avatar}
          sx={{ width: 24, height: 24 }}
          alt={item.title}
        />
      );
    }

    // Handle asset thumbnails
    if (item.type === "asset" && item.thumbnail) {
      return (
        <Avatar
          src={resolveImageUrl(item.thumbnail)}
          sx={{ width: 24, height: 24 }}
          alt={item.title}
          variant="rounded"
        >
          {/* Fallback to icon if image fails to load */}
          <Package
            size={16}
            style={{ color: "var(--mui-palette-text-secondary)" }}
          />
        </Avatar>
      );
    }

    // Fallback to icons for all other cases
    return getIcon(item.type);
  };

  const handleItemClick = (item) => {
    // Handle navigation based on item type
    switch (item.type) {
      case "asset":
        navigate(`/assets/${item.id}`);
        break;
      case "source":
        navigate(`/sources/${item.id}`);
        break;
      case "member":
        navigate(`/members/${item.id}`);
        break;
      default:
        console.log("Unknown item type:", item.type);
    }

    // Call the parent's onItemClick if provided
    onItemClick?.(item);
  };

  if (!isOpen) return null;

  return (
    <Paper
      elevation={2}
      data-cy="search-dropdown"
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 1300,
        maxHeight: 400,
        overflow: "auto",
        mt: 1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <List sx={{ py: 1 }} data-cy="search-results-list">
        {loading ? (
          <ListItem>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                py: 2,
              }}
            >
              <CircularProgress size={20} />
            </Box>
          </ListItem>
        ) : searchResults.length > 0 ? (
          searchResults.map((item) => (
            <ListItem
              key={`${item.type}-${item.id}`}
              onClick={() => handleItemClick(item)}
              sx={{
                py: 1.5,
                px: 2,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                cursor: "pointer",
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {renderItemIcon(item)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="bodyMd" component="p">
                    {item.title}
                  </Typography>
                }
                secondary={
                  item.subtitle && (
                    <Typography
                      variant="bodySm"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {item.subtitle}
                    </Typography>
                  )
                }
              />
            </ListItem>
          ))
        ) : searchQuery.length >= 2 ? (
          <ListItem>
            <ListItemText
              primary={
                <Typography
                  variant="bodySm"
                  component="p"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    py: 2,
                  }}
                >
                  No results found for "{searchQuery}"
                </Typography>
              }
            />
          </ListItem>
        ) : null}
      </List>
    </Paper>
  );
};

export default SearchDropdown;
